import {
  MULTI_NOTE_DETECTOR_PROMPT,
  NOTE_TYPE_CLASSIFIER_PROMPT,
  GENERATION_PROMPTS,
  TRANSCRIPT_CLEANUP_PROMPT,
  SHIFT_HANDOVER_COMPILE_PROMPT,
  SHIFT_HANDOVER_VOICE_MERGE_PROMPT,
  ARABIC_PATIENT_COPY_PROMPT,
} from '../constants/prompts';
import { parseJsonFromResponse } from '../utils/jsonValidator';
import { ADMISSIONS_ENABLED } from '../constants/features';
import { apiPost } from '../config/api';

const VALID_NOTE_TYPES = [
  'progress_note',
  'vitals',
  'handover',
  'medication_admin',
  'fluid_balance',
  'admission',
  'discharge',
  'care_plan',
];

const EXCLUSIVE_NOTE_TYPES = ['handover', 'admission', 'discharge'];

const COMBINED_NOTE_ORDER = ['progress_note', 'medication_admin', 'vitals', 'fluid_balance'];
const CARE_PLAN_COMBINED_ORDER = ['care_plan', 'medication_admin', 'vitals', 'fluid_balance'];

function parseNoteTypesFromResponse(content) {
  if (!content?.trim()) return [];

  let cleaned = content.trim();
  if (cleaned.startsWith('```')) {
    cleaned = cleaned.replace(/^```(?:json)?\n?/, '').replace(/\n?```$/, '');
  }

  const start = cleaned.indexOf('[');
  const end = cleaned.lastIndexOf(']');
  if (start !== -1 && end !== -1) {
    try {
      const parsed = JSON.parse(cleaned.slice(start, end + 1));
      if (Array.isArray(parsed)) {
        return parsed
          .map((type) => String(type).toLowerCase().replace(/[^a-z_]/g, ''))
          .filter((type) => VALID_NOTE_TYPES.includes(type));
      }
    } catch {
      // fall through to single-type parse
    }
  }

  const normalized = cleaned.toLowerCase().replace(/[^a-z_]/g, '');
  if (VALID_NOTE_TYPES.includes(normalized)) {
    return [normalized];
  }

  return [];
}

export function normalizeDetectedNoteTypes(types) {
  let unique = [...new Set(types.filter((type) => VALID_NOTE_TYPES.includes(type)))];
  if (!ADMISSIONS_ENABLED) {
    unique = unique.filter((type) => type !== 'admission');
  }
  const exclusive = unique.filter((type) => EXCLUSIVE_NOTE_TYPES.includes(type));
  if (exclusive.length > 0) {
    return [exclusive[0]];
  }

  if (unique.includes('care_plan')) {
    const withoutProgress = unique.filter((type) => type !== 'progress_note');
    const combined = withoutProgress.filter((type) => CARE_PLAN_COMBINED_ORDER.includes(type));
    if (combined.length > 0) {
      return CARE_PLAN_COMBINED_ORDER.filter((type) => combined.includes(type));
    }
    return ['care_plan'];
  }

  // Clinical requirement: every medication administration must include a progress note.
  if (unique.includes('medication_admin') && !unique.includes('progress_note')) {
    unique.push('progress_note');
  }

  const combined = unique.filter((type) => COMBINED_NOTE_ORDER.includes(type));
  if (combined.length > 0) {
    return COMBINED_NOTE_ORDER.filter((type) => combined.includes(type));
  }

  return unique.length > 0 ? unique : [];
}

export async function callOpenAI(messages, temperature = 0.1, maxTokens = 2000) {
  const data = await apiPost('/api/openai/chat', {
    messages,
    temperature,
    max_tokens: maxTokens,
  });
  return data.choices?.[0]?.message?.content?.trim() || '';
}

export async function cleanTranscript(rawTranscript) {
  if (!rawTranscript?.trim()) return '';

  const content = await callOpenAI(
    [
      { role: 'system', content: TRANSCRIPT_CLEANUP_PROMPT },
      { role: 'user', content: rawTranscript },
    ],
    0.1,
    1000
  );

  return content || rawTranscript;
}

async function detectNoteTypeWithSingleClassifier(transcript) {
  const content = await callOpenAI(
    [
      { role: 'system', content: NOTE_TYPE_CLASSIFIER_PROMPT },
      { role: 'user', content: transcript },
    ],
    0
  );

  const normalized = content.toLowerCase().replace(/[^a-z_]/g, '');
  return VALID_NOTE_TYPES.includes(normalized) ? normalized : null;
}

async function detectNoteTypesForTranscript(transcript) {
  const content = await callOpenAI(
    [
      { role: 'system', content: MULTI_NOTE_DETECTOR_PROMPT },
      { role: 'user', content: transcript },
    ],
    0
  );

  const parsed = normalizeDetectedNoteTypes(parseNoteTypesFromResponse(content));
  if (parsed.length) return parsed;

  const single = await detectNoteTypeWithSingleClassifier(transcript);
  return single ? normalizeDetectedNoteTypes([single]) : [];
}

export async function detectNoteTypes(transcript, alternateTranscript = null) {
  const primary = String(transcript || '').trim();
  if (!primary) return [];

  let types = await detectNoteTypesForTranscript(primary);
  const alternate = String(alternateTranscript || '').trim();
  if (!types.length && alternate && alternate !== primary) {
    types = await detectNoteTypesForTranscript(alternate);
  }
  return types;
}

export async function detectNoteType(transcript) {
  const types = await detectNoteTypes(transcript);
  return types[0] || null;
}

export async function generateNote(transcript, noteType) {
  const systemPrompt = GENERATION_PROMPTS[noteType] || GENERATION_PROMPTS.progress_note;

  const content = await callOpenAI([
    { role: 'system', content: systemPrompt },
    { role: 'user', content: `Transcript:\n${transcript}` },
  ]);

  const parsed = parseJsonFromResponse(content);
  parsed.note_type = noteType;
  parsed.datetime = parsed.datetime || new Date().toISOString();

  if (!parsed.patient) {
    parsed.patient = {
      name: null,
      room: null,
      age: null,
      sex: null,
      details_source: 'transcript',
    };
  } else {
    parsed.patient.details_source = 'transcript';
  }

  return parsed;
}

/** Simplified Arabic take-home discharge summary for the patient. */
export async function generateArabicPatientCopy(dischargeNote) {
  const content = await callOpenAI(
    [
      { role: 'system', content: ARABIC_PATIENT_COPY_PROMPT },
      {
        role: 'user',
        content: `Discharge note JSON:\n${JSON.stringify(dischargeNote || {}, null, 2)}`,
      },
    ],
    0.2,
    2500
  );

  const parsed = parseJsonFromResponse(content);
  return {
    diagnosis: typeof parsed?.diagnosis === 'string' ? parsed.diagnosis : '',
    medications: Array.isArray(parsed?.medications)
      ? parsed.medications.map((med) => ({
          name: med?.name || '',
          dose: med?.dose || '',
          how_to_take: med?.how_to_take || '',
          when_to_take: med?.when_to_take || '',
        }))
      : [],
    home_instructions: Array.isArray(parsed?.home_instructions)
      ? parsed.home_instructions.filter((item) => typeof item === 'string' && item.trim())
      : [],
    return_to_er: Array.isArray(parsed?.return_to_er)
      ? parsed.return_to_er.filter((item) => typeof item === 'string' && item.trim())
      : [],
    follow_up: typeof parsed?.follow_up === 'string' ? parsed.follow_up : '',
  };
}

export async function generateShiftHandoverDraft(compiledData) {
  const content = await callOpenAI(
    [
      { role: 'system', content: SHIFT_HANDOVER_COMPILE_PROMPT },
      { role: 'user', content: compiledData },
    ],
    0.2,
    2000
  );

  return parseJsonFromResponse(content);
}

export async function assessFluidBalanceConcern(netMl, hours) {
  const content = await callOpenAI(
    [
      {
        role: 'user',
        content: `Is a fluid balance of ${netMl}ml over ${hours} hours clinically concerning for a hospital patient? Reply with one of: normal | monitor | concerning. One word only.`,
      },
    ],
    0,
    10
  );

  const word = (content || 'normal').trim().toLowerCase();
  if (word.includes('concerning')) return 'concerning';
  if (word.includes('monitor')) return 'monitor';
  return 'normal';
}

const NURSE_REVIEW_CORRECTION_SYSTEM = `Update the existing nursing notes based on the spoken correction.
Return ONLY valid JSON with this shape:
{ "notes": [ { "type": "progress_note|vitals|medication_admin|handover|fluid_balance|admission|discharge|care_plan", "note": { ... } } ] }

Rules:
- Include every note type from the input. Do not add or remove note types.
- Only change fields the nurse corrected. Leave every other field exactly as it was.
- If the nurse says a new vital (e.g. BP 130/80), update only that vital on the vitals note (or vitals_extracted on a progress note). Do not rewrite SOAP/narrative unless they corrected that text.
- Do not invent new clinical content.
- Write any changed clinical text in English.
- Translate Arabic or mixed speech into English field values.
- Return the full note objects, not diffs.`;

export function applyCorrectedNurseNotes(existingNotes, updatedNotes) {
  const list = Array.isArray(updatedNotes) ? updatedNotes : [];
  const byType = new Map();
  list.forEach((entry) => {
    const type = entry?.type;
    const note = entry?.note;
    if (type && note && typeof note === 'object') {
      byType.set(type, note);
    }
  });
  return (existingNotes || []).map((entry) => {
    if (!entry?.note) return entry;
    const next = byType.get(entry.type);
    if (!next) return entry;
    const merged = {
      ...entry.note,
      ...next,
      note_type: entry.note.note_type || entry.type,
      patient: next.patient || entry.note.patient,
    };
    if (entry.note.vitals || next.vitals) {
      merged.vitals = { ...entry.note.vitals, ...next.vitals };
    }
    if (entry.note.vitals_extracted || next.vitals_extracted) {
      merged.vitals_extracted = { ...entry.note.vitals_extracted, ...next.vitals_extracted };
    }
    return {
      ...entry,
      note: merged,
      error: null,
    };
  });
}

/** Patch existing review notes from a spoken correction (same idea as doctor review). */
export async function updateNurseNotesFromCorrection(existingNotes, correctionTranscript) {
  const payload = (existingNotes || [])
    .filter((entry) => entry?.type && entry?.note)
    .map((entry) => ({ type: entry.type, note: entry.note }));
  if (!payload.length) return existingNotes || [];

  const content = await callOpenAI(
    [
      { role: 'system', content: NURSE_REVIEW_CORRECTION_SYSTEM },
      {
        role: 'user',
        content: `Existing notes JSON:
${JSON.stringify({ notes: payload }, null, 2)}

Nurse correction:
${String(correctionTranscript || '').trim()}`,
      },
    ],
    0.1,
    2800
  );

  const parsed = parseJsonFromResponse(content);
  const updated = Array.isArray(parsed?.notes)
    ? parsed.notes
    : Array.isArray(parsed)
      ? parsed
      : null;
  if (!updated) {
    throw new Error('Failed to parse updated notes');
  }
  return applyCorrectedNurseNotes(existingNotes, updated);
}

export async function mergeHandoverVoiceNote(existingHandover, voiceTranscript) {
  const content = await callOpenAI(
    [
      { role: 'system', content: SHIFT_HANDOVER_VOICE_MERGE_PROMPT },
      {
        role: 'user',
        content: JSON.stringify({
          existing: existingHandover,
          voice_note: voiceTranscript,
        }),
      },
    ],
    0.2,
    2000
  );

  return parseJsonFromResponse(content);
}
